/**
 * StatKpisHost — data-source Host for the STAT_KPIS builtin. GLOBAL
 * (accountId null): fetchPortfolioDashboard + useDeliveryDashboard(null),
 * same as PortfolioDashboardPage's KPI band today. ACCOUNT: useAccountDashboard
 * + useDeliveryDashboard(accountId), same as AccountDashboardPage's KPI band
 * today (note the different finish-date label/testid/open-RAID copy — an
 * exact parity requirement, not cosmetic drift). No recharts — loads eagerly.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchPortfolioDashboard, useAccountDashboard, useDeliveryDashboard } from '@/api/dashboard';
import { WidgetBoundary } from '../WidgetBoundary';
import { StatKpiBand } from '../StatKpiBand';
import { WidgetSkeleton } from '../WidgetSkeleton';
import type { BuiltinHostProps } from './hostProps';

export function StatKpisHost({ title, minHeight, accountId }: BuiltinHostProps) {
  const portfolio = useQuery({
    queryKey: ['portfolio-dashboard'],
    queryFn: fetchPortfolioDashboard,
    enabled: !accountId,
    staleTime: 30_000,
  });
  const account = useAccountDashboard(accountId ?? '');
  const delivery = useDeliveryDashboard(accountId);

  const isLoading = accountId ? account.isLoading : portfolio.isLoading;
  const isError = accountId ? account.isError : portfolio.isError;

  return (
    <WidgetBoundary label={title} minHeight={minHeight}>
      {isLoading && <WidgetSkeleton />}
      {!isLoading && isError && <WidgetSkeleton variant="error" label={title} />}
      {!isLoading && !isError && accountId && account.data && (
        <StatKpiBand
          totalProjects={account.data.projects.filter((p) => p.status !== 'CANCELLED').length}
          activeProjects={account.data.projects.filter((p) => p.status === 'ACTIVE').length}
          crsDoneLast30={delivery.data?.crsDoneLast30 ?? 0}
          finishDate={account.data.portfolioFinishDate}
          finishLabel="Forecast Finish"
          finishTestId="account-forecast-finish"
          openRaidCount={account.data.openRaidCount}
          openRaidSub="open"
        />
      )}
      {!isLoading && !isError && !accountId && portfolio.data && (
        <StatKpiBand
          totalProjects={portfolio.data.totalProjects}
          activeProjects={portfolio.data.projects.filter((p) => p.status === 'ACTIVE').length}
          crsDoneLast30={delivery.data?.crsDoneLast30 ?? 0}
          finishDate={portfolio.data.portfolioFinishDate}
          finishLabel="Portfolio Finish"
          finishTestId="portfolio-finish-date"
          openRaidCount={portfolio.data.totalOpenRaidItems}
          openRaidSub="across portfolio"
        />
      )}
    </WidgetBoundary>
  );
}
